import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import type {
  FilterField,
  FilterInputType,
  FilterLine as FilterLineType,
  FilterOperator,
  FilterOption,
  FilterValue,
} from '../types';
import { hasSelectedField, hasSelectedOperator } from '../vyTableFilterUtils';
import { FilterDeleteButton } from './FilterDeleteButton';
import { FilterFieldSelect } from './FilterFieldSelect';
import { FilterLineLabel } from './FilterLineLabel';
import { FilterOperatorSelect } from './FilterOperatorSelect';
import { FilterValueInput } from './FilterValueInput';

type FilterLineProps = {
  filter: FilterLineType;
  availableFields: FilterField[];
  operators: FilterOperator[];
  inputType: FilterInputType;
  options: FilterOption[];
  onUpdateFilter: (field: keyof FilterLineType, value: FilterValue) => void;
  onDeleteFilter: () => void;
  isFirstLine: boolean;
};

export const FilterLine: React.FC<FilterLineProps> = React.memo(
  ({
    filter,
    availableFields,
    operators,
    inputType,
    options,
    onUpdateFilter,
    onDeleteFilter,
    isFirstLine,
  }) => {
    const handleFieldChange = useCallback(
      (value: string) => {
        onUpdateFilter('field', value);
      },
      [onUpdateFilter],
    );

    const handleOperatorChange = useCallback(
      (value: string) => {
        onUpdateFilter('operator', value);
        onUpdateFilter('value', []);
      },
      [onUpdateFilter],
    );

    const handleValueChange = useCallback(
      (value: FilterValue) => {
        onUpdateFilter('value', value);
      },
      [onUpdateFilter],
    );

    useEffect(() => {
      if (hasSelectedField(filter) && operators.length >= 1 && !filter.operator) {
        onUpdateFilter('operator', operators[0].value);
      }
    }, [filter.field, operators, filter.operator, onUpdateFilter]);

    const hasRequiredSelections = useMemo(() => {
      return hasSelectedField(filter) && hasSelectedOperator(filter);
    }, [filter]);

    const isSingleOptionField = useMemo(() => {
      return (inputType === 'boolean' || inputType === 'select') && options.length === 1;
    }, [inputType, options]);

    const hasNoValueSelected = useMemo(() => {
      return !filter.value || (Array.isArray(filter.value) && filter.value.length === 0);
    }, [filter.value]);

    const shouldAutoSelectValue = useMemo(() => {
      return hasRequiredSelections && isSingleOptionField && hasNoValueSelected;
    }, [hasRequiredSelections, isSingleOptionField, hasNoValueSelected]);

    useEffect(() => {
      if (shouldAutoSelectValue) {
        if (inputType === 'boolean') {
          onUpdateFilter('value', options[0].value === 'true');
        } else {
          onUpdateFilter('value', [options[0].value]);
        }
      }
    }, [shouldAutoSelectValue, inputType, options, onUpdateFilter]);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FilterLineLabel isFirstLine={isFirstLine} />

        <FilterFieldSelect
          field={filter.field}
          availableFields={availableFields}
          onFieldChange={handleFieldChange}
        />

        <FilterOperatorSelect
          operator={filter.operator}
          operators={operators}
          disabled={!hasSelectedField(filter)}
          onOperatorChange={handleOperatorChange}
        />

        <FilterValueInput
          inputType={inputType}
          value={filter.value}
          options={options}
          disabled={!hasSelectedOperator(filter)}
          onValueChange={handleValueChange}
        />

        <FilterDeleteButton onDelete={onDeleteFilter} />
      </Box>
    );
  },
);
